import { useCallback, useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Check, Clock, Copy, Loader2, QrCode } from "lucide-react"

interface PixPaymentProps {
  qrCode?: string
  qrCodeUrl?: string
  expiresAt?: string
  onPaymentConfirmed?: () => void
  onCheckStatus?: () => Promise<boolean>
}

export function PixPayment({
  qrCode,
  qrCodeUrl,
  expiresAt,
  onPaymentConfirmed,
  onCheckStatus,
}: PixPaymentProps) {
  const [copied, setCopied] = useState(false)
  const [timeLeft, setTimeLeft] = useState("")
  const [isExpired, setIsExpired] = useState(false)
  const [isChecking, setIsChecking] = useState(false)

  // Timer countdown
  useEffect(() => {
    if (!expiresAt) return

    const interval = setInterval(() => {
      const now = new Date().getTime()
      const expiry = new Date(expiresAt).getTime()
      const diff = expiry - now

      if (diff <= 0) {
        setIsExpired(true)
        setTimeLeft("Expirado")
        clearInterval(interval)
        return
      }

      const minutes = Math.floor(diff / 60000)
      const seconds = Math.floor((diff % 60000) / 1000)
      setTimeLeft(`${minutes}:${seconds.toString().padStart(2, "0")}`)
    }, 1000)

    return () => clearInterval(interval)
  }, [expiresAt])

  // Polling for payment confirmation
  useEffect(() => {
    if (!onCheckStatus || isExpired) return

    const pollInterval = setInterval(async () => {
      setIsChecking(true)
      try {
        const confirmed = await onCheckStatus()
        if (confirmed) {
          clearInterval(pollInterval)
          onPaymentConfirmed?.()
        }
      } catch {
        // Silently continue polling
      } finally {
        setIsChecking(false)
      }
    }, 5000)

    return () => clearInterval(pollInterval)
  }, [onCheckStatus, isExpired, onPaymentConfirmed])

  const handleCopy = useCallback(() => {
    if (qrCode) {
      navigator.clipboard.writeText(qrCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }, [qrCode])

  if (!qrCode && !qrCodeUrl) {
    return (
      <div className="flex flex-col items-center gap-4 py-8">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        <p className="text-sm text-gray-500">Gerando código PIX...</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center gap-6 py-4">
      <div className="text-center">
        <h3 className="text-lg font-semibold">Pagamento via PIX</h3>
        <p className="text-sm text-gray-500">Escaneie o QR Code ou copie o código para pagar</p>
      </div>

      {qrCodeUrl ? (
        <div className="rounded-lg border bg-white p-4">
          <img src={qrCodeUrl} alt="QR Code PIX" className="h-48 w-48" />
        </div>
      ) : (
        <div className="flex h-48 w-48 items-center justify-center rounded-lg border bg-gray-50">
          <QrCode className="h-16 w-16 text-gray-300" />
        </div>
      )}

      {!isExpired && timeLeft && (
        <div className="flex items-center gap-2 text-sm text-amber-600">
          <Clock className="h-4 w-4" />
          <span>Expira em {timeLeft}</span>
        </div>
      )}

      {isExpired && (
        <div className="text-sm font-medium text-red-500">Código PIX expirado. Gere um novo.</div>
      )}

      {qrCode && !isExpired && (
        <div className="w-full space-y-2">
          <div className="rounded-md border bg-gray-50 p-3">
            <p className="text-xs break-all text-gray-600">{qrCode}</p>
          </div>
          <Button variant="outline" className="w-full" onClick={handleCopy} disabled={isExpired}>
            {copied ? (
              <>
                <Check className="mr-2 h-4 w-4 text-green-500" />
                Copiado!
              </>
            ) : (
              <>
                <Copy className="mr-2 h-4 w-4" />
                Copiar código PIX
              </>
            )}
          </Button>
        </div>
      )}

      {isChecking && <p className="text-xs text-gray-400">Verificando pagamento...</p>}
    </div>
  )
}
